/* global window, document */
/**
 * Storefront controller for the EU withdrawal form.
 *
 * Flow:
 *   1. User submits full name + order# + email → POST /apps/withdrawal/validate
 *      • Live status shows "Checking subscription(s)…"
 *      • On success, status shows "X subscription(s) found!" (green)
 *        and the confirm block appears with "Cancel subscription" enabled.
 *   2. On confirm → POST /apps/withdrawal/cancel → success state.
 *      A single order can hold multiple Appstle contracts (yearly +
 *      monthly bought together): all eligible ones are cancelled.
 *
 * Errors arrive as structured codes (order_not_found, email_mismatch,
 * no_contract, out_of_window, rate_limited, internal_error). Each code
 * maps to `withdrawal.errors.<code>` for localised copy.
 *
 * The full name is included in the payload because the backend records
 * it in the email acknowledgement as the consumer's declared identity
 * (legal record for Article 11(3) of Directive 2011/83/EU).
 */
(function () {
  const form = document.querySelector("[data-withdrawal-form]");
  if (!form) return;

  // ---------- i18n strings (with English fallbacks) ----------
  const i18n = {
    checking: form.dataset.i18nChecking || "Checking subscription(s)…",
    cancelling: form.dataset.i18nCancelling || "Cancelling…",
    foundOne: form.dataset.i18nFoundOne || "1 subscription found!",
    foundMany: form.dataset.i18nFoundMany || "{{count}} subscriptions found!",
    confirmBodyOne:
      form.dataset.i18nConfirmBodyOne ||
      "You're about to cancel the subscription linked to order {{order}}. This cannot be undone.",
    confirmBodyMany:
      form.dataset.i18nConfirmBodyMany ||
      "You're about to cancel {{count}} subscriptions linked to order {{order}}. This cannot be undone.",
    successBodyOne:
      form.dataset.i18nSuccessBodyOne ||
      "Your subscription has been cancelled. A confirmation email will be sent shortly.",
    successBodyMany:
      form.dataset.i18nSuccessBodyMany ||
      "All {{count}} subscriptions linked to your order have been cancelled. A confirmation email will be sent shortly.",
    successBodyPartial:
      form.dataset.i18nSuccessBodyPartial ||
      "{{cancelled}} of {{total}} subscriptions were cancelled. Please contact support if any remain active.",
    errors: {
      order_not_found:
        form.dataset.i18nErrOrderNotFound || "We couldn't find an order with that number.",
      email_mismatch:
        form.dataset.i18nErrEmailMismatch || "The email doesn't match the one on the order.",
      no_contract:
        form.dataset.i18nErrNoContract || "This order isn't linked to an active subscription.",
      out_of_window:
        form.dataset.i18nErrOutOfWindow || "The 14-day withdrawal period has expired.",
      invalid_request:
        form.dataset.i18nErrInvalidRequest ||
        "Please check the form and try again.",
      internal_error:
        form.dataset.i18nErrInternal || "Something went wrong. Please try again later.",
      forbidden: form.dataset.i18nErrForbidden || "Request not allowed.",
      rate_limited:
        form.dataset.i18nErrRateLimited ||
        "Too many requests. Please wait a moment and try again.",
    },
  };

  // ---------- DOM refs ----------
  const nameInput = form.querySelector("[data-withdrawal-name]");
  const orderInput = form.querySelector("[data-withdrawal-order]");
  const emailInput = form.querySelector("[data-withdrawal-email]");
  const submitBtn = form.querySelector("[data-withdrawal-submit]");
  const loadingEl = form.querySelector("[data-withdrawal-loading]");
  const statusEl = form.querySelector("[data-withdrawal-status]");
  const confirmBlock = form.querySelector("[data-withdrawal-confirm]");
  const confirmBody = form.querySelector("[data-withdrawal-confirm-body]");
  const successBlock = form.querySelector("[data-withdrawal-success]");
  const successBody = form.querySelector("[data-withdrawal-success-body]");
  const cancelActionBtns = form.querySelectorAll("[data-withdrawal-cancel-action]");
  const confirmBtn = form.querySelector(
    "[data-withdrawal-cancel-action='confirm']",
  );

  const validateUrl = form.dataset.validateUrl;
  const cancelUrl = form.dataset.cancelUrl;

  /** @type {{orderName:string,email:string,customerName:string,eligibleCount:number} | null} */
  let validatedPayload = null;

  // ---------- helpers ----------
  function fmt(template, vars) {
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
      Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : "",
    );
  }

  function setStatus(message, state) {
    if (!statusEl) return;
    statusEl.textContent = message || "";
    if (state) statusEl.setAttribute("data-state", state);
    else statusEl.removeAttribute("data-state");
  }

  function setLoading(isLoading) {
    submitBtn.setAttribute("aria-busy", isLoading ? "true" : "false");
    submitBtn.disabled = isLoading;
    if (loadingEl) loadingEl.hidden = !isLoading;
  }

  function setConfirmEnabled(enabled) {
    if (!confirmBtn) return;
    confirmBtn.disabled = !enabled;
    confirmBtn.setAttribute("aria-disabled", enabled ? "false" : "true");
  }

  function hideConfirm() {
    if (confirmBlock) confirmBlock.hidden = true;
    setConfirmEnabled(false);
  }

  function showConfirm(count, orderName) {
    if (confirmBody) {
      const tmpl = count > 1 ? i18n.confirmBodyMany : i18n.confirmBodyOne;
      confirmBody.textContent = fmt(tmpl, { count, order: orderName });
    }
    if (confirmBlock) confirmBlock.hidden = false;
    setConfirmEnabled(true);
  }

  function showSuccess({ cancelledCount, totalAttempted, partial }) {
    if (successBody) {
      let tmpl;
      let vars;
      if (partial) {
        tmpl = i18n.successBodyPartial;
        vars = { cancelled: cancelledCount, total: totalAttempted };
      } else if (cancelledCount > 1) {
        tmpl = i18n.successBodyMany;
        vars = { count: cancelledCount };
      } else {
        tmpl = i18n.successBodyOne;
        vars = { count: cancelledCount };
      }
      successBody.textContent = fmt(tmpl, vars);
    }
    if (successBlock) successBlock.hidden = false;
  }

  function errorMessage(code, deadline) {
    let msg = i18n.errors[code] || i18n.errors.internal_error;
    if (code === "out_of_window" && deadline) {
      try {
        const d = new Date(deadline);
        msg = msg + " (" + d.toLocaleDateString() + ")";
      } catch (_) {
        /* ignore */
      }
    }
    return msg;
  }

  async function postJson(url, body) {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    let data = null;
    try {
      data = await res.json();
    } catch (_) {
      /* non-JSON response */
    }
    return { status: res.status, data };
  }

  function resetUiBeforeValidate() {
    hideConfirm();
    if (successBlock) successBlock.hidden = true;
    validatedPayload = null;
  }

  // ---------- handlers ----------
  async function handleValidate(event) {
    event.preventDefault();
    resetUiBeforeValidate();

    const customerName = (nameInput.value || "").trim();
    const orderName = (orderInput.value || "").trim();
    const email = (emailInput.value || "").trim();
    if (!customerName || !orderName || !email) {
      setStatus(errorMessage("invalid_request"), "error");
      return;
    }

    setLoading(true);
    setStatus(i18n.checking, "info");

    try {
      const { data } = await postJson(validateUrl, {
        customerName,
        orderName,
        email,
      });
      if (!data || data.ok !== true) {
        setStatus(
          errorMessage((data && data.code) || "internal_error", data && data.deadline),
          "error",
        );
        return;
      }

      const eligibleCount = Number(data.eligibleCount || 0);
      if (eligibleCount <= 0) {
        setStatus(errorMessage("out_of_window", data.deadline), "error");
        return;
      }

      validatedPayload = { customerName, orderName, email, eligibleCount };
      const foundMsg =
        eligibleCount === 1
          ? i18n.foundOne
          : fmt(i18n.foundMany, { count: eligibleCount });
      setStatus(foundMsg, "success");
      showConfirm(eligibleCount, orderName);
    } catch (err) {
      setStatus(errorMessage("internal_error"), "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmAction(action) {
    if (action === "abort") {
      validatedPayload = null;
      hideConfirm();
      setStatus("", null);
      return;
    }
    if (!validatedPayload) return;
    if (confirmBtn && confirmBtn.disabled) return;

    setLoading(true);
    setConfirmEnabled(false);
    setStatus(i18n.cancelling, "info");

    try {
      const { data } = await postJson(cancelUrl, {
        customerName: validatedPayload.customerName,
        orderName: validatedPayload.orderName,
        email: validatedPayload.email,
      });
      if (!data || data.ok !== true) {
        setStatus(
          errorMessage((data && data.code) || "internal_error", data && data.deadline),
          "error",
        );
        setConfirmEnabled(true);
        return;
      }

      const cancelledCount = Number(data.cancelledCount || 0);
      const totalAttempted = (data.outcomes && data.outcomes.length) || cancelledCount;
      const partial = Boolean(data.partial);

      validatedPayload = null;
      hideConfirm();
      setStatus("", null);
      showSuccess({ cancelledCount, totalAttempted, partial });

      form.reset();
    } catch (err) {
      setStatus(errorMessage("internal_error"), "error");
      setConfirmEnabled(true);
    } finally {
      setLoading(false);
    }
  }

  // Reset state if the user edits any field after a successful validate.
  const invalidateOnEdit = () => {
    if (!validatedPayload && (!confirmBlock || confirmBlock.hidden)) return;
    hideConfirm();
    if (successBlock) successBlock.hidden = true;
    validatedPayload = null;
    setStatus("", null);
  };
  nameInput.addEventListener("input", invalidateOnEdit);
  orderInput.addEventListener("input", invalidateOnEdit);
  emailInput.addEventListener("input", invalidateOnEdit);

  form.addEventListener("submit", handleValidate);

  cancelActionBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      handleConfirmAction(btn.getAttribute("data-withdrawal-cancel-action"));
    });
  });

  // Initial state: cancel button disabled until validation succeeds.
  setConfirmEnabled(false);
})();
